import {forwardRef} from 'react';
import {Button, Card, Link, Typography} from '@ezcater/tapas';
import useTranslation from 'next-translate/useTranslation';
import {twMerge} from 'tailwind-merge';

import {CuisineTaxonEnum} from '@/components/features/search/filters/FiltersUtils/cuisines';
import {
  JumpBackInHomepageExperimentVariation,
  useJumpBackInHomepage,
} from '@/components/global/providers/JumpBackInHomepageProvider/JumpBackInHomepageProvider';
import Carousel, {CarouselCard} from '@/components/ui/Carousel';
import useTracking from '@/hooks/useTracking';
import useValuePropsExperiment from '@/hooks/useValuePropsExperiment';
import {SEARCH_NEW_PATH} from '@/paths';
import {compilePath} from '@/utils';
import {geocodedAddressResultToStartNewSearchAddressParams} from '@/utils/geocodedAddressResult';
import {type GeocodedAddressResult} from '@/utils/googleAutocomplete';
import RestaurantSearchTile from './RestaurantSearchTile';

export type BrowseAllFilterParams = {
  cuisineOption?: CuisineTaxonEnum;
};

export type RestaurantTileContent = {
  id: string;
  name: string;
  imageUrl: string;
  logoImageUrl?: string;
};

export type CollectionOfFiltersCard = {
  header: string;
  browseAllFilterParams?: BrowseAllFilterParams;
  items: readonly RestaurantTileContent[];
  tileVariant?: 'photo' | 'logo';
};

type CardContentsProps = {
  card: CollectionOfFiltersCard;
  recommendedDefaultAddress?: GeocodedAddressResult | null;
  loading?: boolean;
  shouldShowJumpBackIn?: boolean;
};

const CardContents: React.FC<CardContentsProps> = ({
  card,
  recommendedDefaultAddress,
  loading = false,
  shouldShowJumpBackIn = false,
}) => {
  const {t} = useTranslation('home');
  const {trackClick} = useTracking();

  const browseAllHref = compilePath(
    SEARCH_NEW_PATH,
    {},
    {
      ...geocodedAddressResultToStartNewSearchAddressParams(recommendedDefaultAddress ?? undefined),
      ...card.browseAllFilterParams,
    },
    {skipEmptyString: true, skipNull: true},
  );

  const handleBrowseAllClick = () => {
    trackClick('collection-of-filters-browse-all', {
      misc_json: JSON.stringify({
        header: card.header,
        cuisineOption: card.browseAllFilterParams?.cuisineOption ?? null,
      }),
    });
  };

  return (
    <Card
      className={twMerge(
        'rounded-3xl border border-peppercorn-200 bg-white p-4 desktop924:p-6',
        shouldShowJumpBackIn && 'border-peppercorn-100',
      )}
    >
      <div className="relative mb-3 flex items-center justify-between gap-2">
        <Typography className="text-base font-bold text-peppercorn-800 desktop924:text-lg">
          {t(card.header)}
        </Typography>
        <Link
          className="!static whitespace-nowrap text-sm before:absolute before:inset-0 before:content-['']"
          data-testid={`collection-of-filters-browse-all-${card.header.split('.').pop()}`}
          href={browseAllHref}
          onClick={handleBrowseAllClick}
        >
          {t('collectionOfFilters.browseAllLinks')}
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {loading
          ? Array.from({length: 4}).map((_, i) => (
              <Card key={i} className="w-full overflow-hidden rounded-xl border-0 p-0 shadow-none">
                <div className="h-[120px] w-[134px] rounded-xl bg-peppercorn-50 desktop924:h-[152px] desktop924:w-[168px]" />
              </Card>
            ))
          : card.items.map((item, index) => (
              <RestaurantSearchTile
                key={item.id}
                item={item}
                cardHeader={card.header}
                tilePosition={index}
                recommendedDefaultAddress={recommendedDefaultAddress}
                variant={card.tileVariant}
              />
            ))}
      </div>
    </Card>
  );
};

type CollectionOfFiltersProps = {
  cards: readonly [CollectionOfFiltersCard, CollectionOfFiltersCard, CollectionOfFiltersCard];
  recommendedDefaultAddress?: GeocodedAddressResult | null;
  loading?: boolean;
};

const CollectionOfFilters = forwardRef<HTMLDivElement, CollectionOfFiltersProps>(
  function CollectionOfFilters({cards, recommendedDefaultAddress, loading = false}, ref) {
    const {t} = useTranslation('home');
    const {track, trackClick} = useTracking();
    const {
      variation: jumpBackInVariation,
      loading: jumpBackInLoading,
      hasFulfillmentDetails,
    } = useJumpBackInHomepage();

    const {isTreatment: inValuePropsTreatment} = useValuePropsExperiment();

    const shouldShowJumpBackIn =
      jumpBackInVariation === JumpBackInHomepageExperimentVariation.TREATMENT &&
      !jumpBackInLoading &&
      hasFulfillmentDetails;

    // ezx579::ARC::ValueProps
    const isJumpBackInOccupyingTop =
      jumpBackInVariation === JumpBackInHomepageExperimentVariation.TREATMENT &&
      (jumpBackInLoading || hasFulfillmentDetails);

    const handleCarouselInteraction = () => {
      track('collection-of-filters-carousel-scroll', {
        url: window.location.href,
      });
    };

    return (
      <div
        className={twMerge(
          shouldShowJumpBackIn && 'mx-auto w-full max-w-[1296px] px-4 tablet:px-6',
          // ezx579::ARC::ValueProps
          inValuePropsTreatment &&
            !isJumpBackInOccupyingTop &&
            'pt-[6.5rem] min-[640px]:pt-[5.5rem] desktop:pt-8',
        )}
      >
        {shouldShowJumpBackIn && (
          <div className="tablet:flex tablet:flex-row tablet:items-center tablet:justify-between">
            <h2 className="text-xl font-bold tablet:text-2xl">
              {t('collectionOfFilters.recommendedForYou')}
            </h2>
            <Button
              className="hidden rounded-full desktop:block desktop:shrink-0"
              render={
                <a
                  aria-label={t('collectionOfFilters.viewAllButton')}
                  data-testid="collection-of-filters-view-all-button"
                  href={compilePath(
                    SEARCH_NEW_PATH,
                    {},
                    {
                      ...geocodedAddressResultToStartNewSearchAddressParams(
                        recommendedDefaultAddress ?? undefined,
                      ),
                    },
                  )}
                  onClick={() => trackClick('collection-of-filters-view-all-button-desktop')}
                >
                  {t('collectionOfFilters.viewAllButton')}
                </a>
              }
              size="medium"
              variant="secondary"
            />
          </div>
        )}
        <Carousel
          ref={ref}
          data-testid="collection-of-filters"
          hideControls
          onScrollChange={handleCarouselInteraction}
          slotProps={{
            slideContainer: {
              className: twMerge(
                'w-full scroll-pl-4 gap-4 pb-8 pl-4 pr-4 min-[640px]:pb-12 tablet:gap-6 desktop924:pb-16 desktop1279:justify-center',
                shouldShowJumpBackIn
                  ? '-mx-4 w-auto max-w-none tablet:-mx-6 tablet:scroll-pl-6 tablet:pl-6 tablet:pr-6 desktop1279:justify-start'
                  : 'pt-4 min-[640px]:pt-8 desktop924:pt-12 desktop1279:pr-0',
              ),
            },
          }}
        >
          {cards.map(card => (
            <CarouselCard
              basisClassNames="h-[326px] w-[312px] desktop924:w-[400px] desktop924:h-[410px]"
              className="p-0 first-of-type:pl-0 last-of-type:pr-0"
              key={card.header}
            >
              <CardContents
                card={card}
                recommendedDefaultAddress={recommendedDefaultAddress}
                loading={loading}
                shouldShowJumpBackIn={shouldShowJumpBackIn}
              />
            </CarouselCard>
          ))}
        </Carousel>
      </div>
    );
  },
);

export default CollectionOfFilters;
